import { ChartPattern, ChartPatternDetectOptions, ChartPatternTargetStatus, getChartPatternTargetStatus, getDoubleBottomTargetStatus, getDoubleTopTargetStatus } from './chartPatternTypes'
import { CHART_PATTERN_DOUBLE_BOTTOM_COLOR, CHART_PATTERN_DOUBLE_TOP_COLOR, CHART_PATTERN_FALLING_WEDGE_COLOR, CHART_PATTERN_RISING_WEDGE_COLOR, CHART_PATTERN_BEARISH_PENNANT_COLOR, CHART_PATTERN_BULLISH_PENNANT_COLOR } from '../../../assets/chartPatternConfig'
import type { Time } from 'lightweight-charts'
import type { Point } from '../../../components/chartV2/drawingTools/drawing.interface'
import {
    buildAlternatingSwings,
    findPivotHighBetween,
    findPivotHighs,
    findPivotLowBetween,
    findPivotLows,
    limitPatterns,
    linePriceAt,
    pivotSlope,
    pricesAreSimilar,
    resolveChartPatternPivotLength,
    resolveLookbackBars,
    shouldIncludePriceTarget,
    sliceRecentOhlc,
    slopeIsNearHorizontal,
    slopesAreParallel,
    slopesAreSymmetric,
    toPatternPoint,
    CHART_PATTERN_PIVOT_LENGTH,
} from './patternUtils'
import { chartPatternSupportsPriceTarget } from '../../../assets/chartPatternConfig'
import { atr as computeAtr } from '../core/indicators'

type Pivot = { index: number; price: number }

/** TradingView auto chart patterns use 5/5 pivots */
const TV_PIVOT = CHART_PATTERN_PIVOT_LENGTH

const TRIANGLE_PATTERN_DEFAULTS = {
    swingLength: 3,
    minBarsForPattern: 8,
    maxBarsForPattern: 60,
    maxBarsForConfirmation: 9,
    atrLength: 14,
    minSwingProminenceAtr: 0.1,
    minTriangleHeightAtr: 0.5,
    maxTriangleHeightAtr: 4,
    maxHorizontalToleranceAtr: 0.5,
}

/** @deprecated use TRIANGLE_PATTERN_DEFAULTS */
const ASCENDING_TRIANGLE_DEFAULTS = TRIANGLE_PATTERN_DEFAULTS

function atrAtBar(atrValues: number[], barIndex: number, atrLength: number): number {
    const k = barIndex - (atrLength - 1)
    if (k < 0 || k >= atrValues.length) return NaN
    return atrValues[k]
}

function swingHighProminence(
    high: number[],
    low: number[],
    index: number,
    swingLength: number,
    minAtrMultiple: number,
    atrValue: number,
): boolean {
    if (!Number.isFinite(atrValue) || atrValue <= 0) return true
    const left = Math.max(0, index - swingLength)
    const right = Math.min(high.length - 1, index + swingLength)
    let minLow = Infinity
    for (let j = left; j <= right; j++) {
        minLow = Math.min(minLow, low[j])
    }
    return high[index] - minLow >= minAtrMultiple * atrValue
}

function swingLowProminence(
    high: number[],
    low: number[],
    index: number,
    swingLength: number,
    minAtrMultiple: number,
    atrValue: number,
): boolean {
    if (!Number.isFinite(atrValue) || atrValue <= 0) return true
    const left = Math.max(0, index - swingLength)
    const right = Math.min(high.length - 1, index + swingLength)
    let maxHigh = -Infinity
    for (let j = left; j <= right; j++) {
        maxHigh = Math.max(maxHigh, high[j])
    }
    return maxHigh - low[index] >= minAtrMultiple * atrValue
}

function tvNumberedLabels(pointIds: string[]) {
    return pointIds.map((pointId, index) => ({ pointId, text: String(index + 1) }))
}

function findNecklineCloseBreakAfter(
    close: number[],
    neckPrice: number,
    afterIdx: number,
    bearish: boolean,
    inProgress: boolean,
): { index: number; price: number; broken: boolean } {
    for (let i = afterIdx + 1; i < close.length; i++) {
        if (bearish ? close[i] < neckPrice : close[i] > neckPrice) {
            return { index: i, price: neckPrice, broken: true }
        }
    }
    if (inProgress) {
        return { index: close.length - 1, price: neckPrice, broken: false }
    }
    return { index: -1, price: neckPrice, broken: false }
}

type DoubleTopCandidate = {
    pattern: ChartPattern
    score: number
    top1Idx: number
    troughIdx: number
    top2Idx: number
    patternHeight: number
    targetStatus: ChartPatternTargetStatus
}

function swingToPivot(swing: { index: number; price: number }): Pivot {
    return { index: swing.index, price: swing.price }
}

function isAwaitingPatternStatus(status: ChartPatternTargetStatus): boolean {
    return status === 'Awaiting' || status === 'In progress'
}

function doubleTopPatternsOverlap(a: ChartPattern, b: ChartPattern): boolean {
    const aStart = (a.points[0]?.x as number) ?? 0
    const aEnd = (a.points[a.points.length - 1]?.x as number) ?? 0
    const bStart = (b.points[0]?.x as number) ?? 0
    const bEnd = (b.points[b.points.length - 1]?.x as number) ?? 0
    return !(aEnd <= bStart || bEnd <= aStart)
}

function buildPatternStatusCandles(
    timestamps: number[],
    high: number[],
    low: number[],
    closes: number[],
) {
    return timestamps.map((t, i) => ({
        time: Math.floor(t / 1000) as Time,
        close: closes[i],
        low: low[i],
        high: high[i],
    }))
}

function filterDoubleTopCandidatesByPatternsFilter(
    candidates: DoubleTopCandidate[],
    patternsFilter: string,
): DoubleTopCandidate[] {
    if (patternsFilter === 'Only Awaiting') {
        return candidates.filter((c) => isAwaitingPatternStatus(c.targetStatus))
    }
    if (patternsFilter === 'Awaiting and Reached') {
        return candidates.filter((c) => c.targetStatus !== 'Failed')
    }
    if (patternsFilter === 'Last Awaiting') {
        const awaiting = candidates
            .filter((c) => isAwaitingPatternStatus(c.targetStatus))
            .sort((a, b) => b.top1Idx - a.top1Idx)
        return awaiting.length ? [awaiting[0]] : []
    }
    return candidates
}

function selectBestDoubleTopCandidate(
    candidates: DoubleTopCandidate[],
    patternsFilter: string,
): DoubleTopCandidate | null {
    const filtered = filterDoubleTopCandidatesByPatternsFilter(candidates, patternsFilter)
    if (!filtered.length) return null

    const sorted = [...filtered].sort((a, b) => {
        const aAwaiting = isAwaitingPatternStatus(a.targetStatus) ? 0 : 1
        const bAwaiting = isAwaitingPatternStatus(b.targetStatus) ? 0 : 1
        if (aAwaiting !== bAwaiting) return aAwaiting - bAwaiting
        // TV shows the dominant pattern on chart — prefer larger pattern height over recency
        if (a.patternHeight !== b.patternHeight) return b.patternHeight - a.patternHeight
        if (a.score !== b.score) return a.score - b.score
        return b.top2Idx - a.top2Idx
    })

    const best = sorted[0]
    for (const candidate of sorted.slice(1)) {
        if (!doubleTopPatternsOverlap(best.pattern, candidate.pattern)) continue
        if (isAwaitingPatternStatus(candidate.targetStatus) && !isAwaitingPatternStatus(best.targetStatus)) {
            return candidate
        }
        if (candidate.score < best.score && isAwaitingPatternStatus(candidate.targetStatus) === isAwaitingPatternStatus(best.targetStatus)) {
            return candidate
        }
    }
    return best
}

/** TV trend height: rise into top 1 vs pattern height (max tops − intermediate min) */
function passesDoubleTopTrendHeight(
    point1: Pivot,
    top1: Pivot,
    top2: Pivot,
    neckPrice: number,
    trendHeightPct: number,
): boolean {
    const patternHeight = Math.max(top1.price, top2.price) - neckPrice
    const precedingTrend = top1.price - point1.price
    if (patternHeight <= 0 || precedingTrend <= 0) return false
    return (precedingTrend / patternHeight) * 100 >= trendHeightPct
}

function findHighestBarBetween(
    values: number[],
    startIdx: number,
    endIdx: number,
): Pivot | null {
    if (endIdx <= startIdx) return null
    let bestIdx = -1
    let bestPrice = -Infinity
    for (let i = startIdx + 1; i <= endIdx; i++) {
        if (values[i] > bestPrice) {
            bestPrice = values[i]
            bestIdx = i
        }
    }
    if (bestIdx < 0) return null
    return { index: bestIdx, price: bestPrice }
}

function findLowestBarBetween(
    values: number[],
    startIdx: number,
    endIdx: number,
): Pivot | null {
    if (endIdx <= startIdx) return null
    let bestIdx = -1
    let bestPrice = Infinity
    for (let i = startIdx + 1; i <= endIdx; i++) {
        if (values[i] < bestPrice) {
            bestPrice = values[i]
            bestIdx = i
        }
    }
    if (bestIdx < 0) return null
    return { index: bestIdx, price: bestPrice }
}

/** TV: permissible deviation compares peak/trough heights measured from the neckline */
function extremumHeightsPassPermissibleDeviation(
    price1: number,
    price2: number,
    neckPrice: number,
    tolerancePct: number,
    fromAbove: boolean,
): boolean {
    const height1 = fromAbove ? price1 - neckPrice : neckPrice - price1
    const height2 = fromAbove ? price2 - neckPrice : neckPrice - price2
    if (height1 <= 0 || height2 <= 0) return false
    const avgHeight = (height1 + height2) / 2
    return (Math.abs(height1 - height2) / avgHeight) * 100 <= tolerancePct
}

/** TV Trend Height: preceding rise must be >= trendHeight% of pattern height */
function passesTrendHeightRequirement(
    startPrice: number,
    topPrice: number,
    neckPrice: number,
    trendHeightPct: number,
): boolean {
    const patternHeight = topPrice - neckPrice
    const precedingTrend = topPrice - startPrice
    if (patternHeight <= 0 || precedingTrend <= 0) return false
    return (precedingTrend / patternHeight) * 100 >= trendHeightPct
}

const TV_DOUBLE_TOP_COLOR = CHART_PATTERN_DOUBLE_TOP_COLOR
const TV_DOUBLE_BOTTOM_COLOR = CHART_PATTERN_DOUBLE_BOTTOM_COLOR
const TV_DOUBLE_PATTERN_FILL_OPACITY = 0.20

function tvDoubleTopPeakLabels(color = TV_DOUBLE_TOP_COLOR) {
    return [
        { pointId: '2', text: 'Top 1', position: 'above' as const, color },
        { pointId: '4', text: 'Top 2', position: 'above' as const, color },
    ]
}

function tvDoubleBottomPeakLabels(color = TV_DOUBLE_BOTTOM_COLOR) {
    return [
        { pointId: '2', text: 'Bottom 1', position: 'below' as const, color },
        { pointId: '4', text: 'Bottom 2', position: 'below' as const, color },
    ]
}

function buildDoubleTopPattern(
    timestamps: number[],
    point1: Pivot,
    top1: Pivot,
    trough: Pivot,
    top2: Pivot,
    point5: { index: number; price: number },
    options: {
        includeTarget: boolean
        inProgressSetting: boolean
        broken: boolean
        formingSecondPeak: boolean
    },
): ChartPattern {
    const height = Math.max(top1.price, top2.price) - trough.price
    const patternInProgress = options.inProgressSetting && !options.broken
    const dottedFromSecondPeak = options.formingSecondPeak && !options.broken

    return {
        kind: 'DOUBLE_TOP',
        bias: 'bearish',
        points: [
            toPatternPoint('1', timestamps[point1.index], point1.price),
            toPatternPoint('2', timestamps[top1.index], top1.price),
            toPatternPoint('3', timestamps[trough.index], trough.price),
            toPatternPoint('4', timestamps[top2.index], top2.price),
            toPatternPoint('5', timestamps[point5.index], point5.price),
        ],
        priceLineSegments: [
            { from: '1', to: '2', style: 'solid', color: TV_DOUBLE_TOP_COLOR },
            { from: '2', to: '3', style: 'solid', color: TV_DOUBLE_TOP_COLOR },
            { from: '3', to: '4', style: dottedFromSecondPeak ? 'dotted' : 'solid', color: TV_DOUBLE_TOP_COLOR },
            { from: '4', to: '5', style: patternInProgress ? 'dotted' : 'solid', color: TV_DOUBLE_TOP_COLOR },
        ],
        polylineIds: ['1', '2', '3', '4', '5'],
        boundaryLines: [{ from: '3', to: '5', style: 'dotted', color: TV_DOUBLE_TOP_COLOR }],
        fillTriangles: undefined,
        fillBackgroundColor: TV_DOUBLE_TOP_COLOR,
        fillBackgroundOpacity: TV_DOUBLE_PATTERN_FILL_OPACITY,
        labels: tvDoubleTopPeakLabels(),
        priceTarget: options.includeTarget ? trough.price - height : undefined,
        targetAnchorPointId: '5',
        inProgress: patternInProgress,
    }
}

function scoreDoubleTopSimilarity(top1Price: number, top2Price: number, neckPrice: number): number {
    const height1 = top1Price - neckPrice
    const height2 = top2Price - neckPrice
    const avgHeight = (height1 + height2) / 2
    if (avgHeight <= 0) return Infinity
    return (Math.abs(height1 - height2) / avgHeight) * 100
}

function findPatternStartPivot(
    pivotLows: Pivot[],
    pivotHighs: Pivot[],
    firstIdx: number,
    pivotLength: number,
    low: number[],
    high: number[],
    preferLow: boolean,
): Pivot {
    if (preferLow) {
        const before = pivotLows.filter((p) => p.index < firstIdx)
        if (before.length) return before[before.length - 1]
    } else {
        const before = pivotHighs.filter((p) => p.index < firstIdx)
        if (before.length) return before[before.length - 1]
    }
    const fallbackIdx = Math.max(0, firstIdx - pivotLength * 2)
    return { index: fallbackIdx, price: preferLow ? low[fallbackIdx] : high[fallbackIdx] }
}

/** TV point 1 — pivot high at the start of the decline into bottom 1, above the midpoint of both lows */
function findDoubleBottomPoint1(
    pivotHighs: Pivot[],
    bottom1: Pivot,
    peak: Pivot,
    bottom2: Pivot,
    low: number[],
    high: number[],
    pivotLength: number,
    trendHeightPct: number,
): Pivot | null {
    const lowsMidpoint = (bottom1.price + bottom2.price) / 2
    const patternHeight = peak.price - Math.min(bottom1.price, bottom2.price)
    const span = Math.max(bottom2.index - bottom1.index, pivotLength * 4)
    const windowStart = Math.max(0, bottom1.index - span)
    const candidates = pivotHighs.filter(
        (p) => p.index >= windowStart && p.index < bottom1.index && p.price > lowsMidpoint,
    )
    if (!candidates.length) return null

    if (patternHeight > 0) {
        const qualifying = candidates.filter((p) => {
            const preceding = p.price - bottom1.price
            return preceding > 0 && (preceding / patternHeight) * 100 >= trendHeightPct
        })
        if (qualifying.length) {
            return qualifying.reduce((best, p) => (p.price > best.price ? p : best))
        }
    }
    return candidates.reduce((best, p) => (p.price > best.price ? p : best))
}

/** TV point 1 — pivot low at the start of the rise into top 1, below the midpoint of both tops */
function findDoubleTopPoint1(
    pivotLows: Pivot[],
    top1: Pivot,
    trough: Pivot,
    top2: Pivot,
    low: number[],
    high: number[],
    pivotLength: number,
    trendHeightPct: number,
): Pivot {
    const patternHeight = Math.max(top1.price, top2.price) - trough.price
    const span = Math.max(top2.index - top1.index, pivotLength * 4)
    const windowStart = Math.max(0, top1.index - span)
    const candidates = pivotLows.filter((p) => p.index >= windowStart && p.index < top1.index)
    if (!candidates.length) {
        return findPatternStartPivot(pivotLows, [], top1.index, pivotLength, low, high, true)
    }
    if (patternHeight > 0) {
        const qualifying = candidates.filter((p) => {
            const preceding = top1.price - p.price
            return preceding > 0 && (preceding / patternHeight) * 100 >= trendHeightPct
        })
        if (qualifying.length) {
            return qualifying[qualifying.length - 1]
        }
    }
    return candidates.reduce((best, p) => (p.price < best.price ? p : best))
}

function resolveIncludePriceTarget(kind: ChartPattern['kind'], priceTargetsFilter?: string): boolean {
    return chartPatternSupportsPriceTarget(kind) && shouldIncludePriceTarget(priceTargetsFilter)
}

function resolveBooleanOption(value: boolean | string | undefined, defaultValue = true): boolean {
    if (value === false || value === 'false') return false
    if (value === true || value === 'true') return true
    return defaultValue
}

function findWedgeTrendlineEndIdx(
    timestamps: number[],
    upperLeft: Pivot,
    upperRight: Pivot,
    lowerLeft: Pivot,
    lowerRight: Pivot,
    fromIdx: number,
    lastBar: number,
): number {
    for (let idx = fromIdx; idx <= lastBar; idx++) {
        const upper = linePriceAt(timestamps, upperLeft, upperRight, idx)
        const lower = linePriceAt(timestamps, lowerLeft, lowerRight, idx)
        if (upper <= lower) return Math.max(fromIdx, idx - 1)
    }
    return lastBar
}

/** TV: close must stay inside the wedge between points 1 (L1) and 4 (H2) */
function closesRespectWedgeBetween(
    closes: number[],
    timestamps: number[],
    upperLeft: Pivot,
    upperRight: Pivot,
    lowerLeft: Pivot,
    lowerRight: Pivot,
    fromIdx: number,
    toIdx: number,
): boolean {
    for (let j = fromIdx; j <= toIdx; j++) {
        const upper = linePriceAt(timestamps, upperLeft, upperRight, j)
        const lower = linePriceAt(timestamps, lowerLeft, lowerRight, j)
        if (closes[j] > upper || closes[j] < lower) return false
    }
    return true
}

function buildRisingWedgePattern(
    timestamps: number[],
    L1: Pivot,
    H1: Pivot,
    L2: Pivot,
    H2: Pivot,
    L3: Pivot,
    high: number[],
    low: number[],
    options: {
        includeTarget: boolean
        inProgressSetting: boolean
        channelWidthRatioPct: number
        l2TolerancePct: number
        closes: number[]
        lastBar: number
        pivotLength: number
    },
): ChartPattern | null {
    const {
        includeTarget,
        inProgressSetting,
        channelWidthRatioPct,
        l2TolerancePct,
        closes,
        lastBar,
        pivotLength,
    } = options
    const wedgeColor = CHART_PATTERN_RISING_WEDGE_COLOR

    if (L2.price <= L1.price * 0.998 || L3.price <= L2.price * 0.998) return null
    if (H2.price <= H1.price * 0.998) return null

    const lowSlope = pivotSlope(L1, L3)
    const highSlope = pivotSlope(H1, H2)
    if (lowSlope <= 0 || highSlope <= 0) return null
    if (lowSlope <= highSlope * 0.95) return null

    const upperAtL1 = linePriceAt(timestamps, H1, H2, L1.index)
    const widthStart = upperAtL1 - L1.price
    if (widthStart <= 0) return null

    const upperAtL3 = linePriceAt(timestamps, H1, H2, L3.index)
    const widthEnd = upperAtL3 - L3.price
    const minNarrowPct = Math.max(1, channelWidthRatioPct) / 100
    if (widthEnd >= widthStart * (1 - minNarrowPct)) return null
    if (upperAtL3 <= L3.price) return null

    if (!closesRespectWedgeBetween(closes, timestamps, H1, H2, L1, L3, L1.index, H2.index)) {
        return null
    }

    const lowerAtL2 = linePriceAt(timestamps, L1, L3, L2.index)
    const channelAtL2 = linePriceAt(timestamps, H1, H2, L2.index) - lowerAtL2
    if (channelAtL2 <= 0) return null
    const l2Dev = (Math.abs(L2.price - lowerAtL2) / channelAtL2) * 100
    if (l2Dev > l2TolerancePct) return null

    let breakoutIdx: number | undefined
    for (let j = L3.index + 1; j <= lastBar; j++) {
        const supportAtJ = linePriceAt(timestamps, L1, L3, j)
        if (closes[j] < supportAtJ) {
            breakoutIdx = j
            break
        }
    }

    if (!breakoutIdx && !inProgressSetting) return null

    const brokenDown = breakoutIdx != null
    const extensionFrom = L3.index + (brokenDown ? 0 : pivotLength)
    const extensionTo = brokenDown && breakoutIdx != null
        ? Math.max(breakoutIdx, L3.index + 1)
        : lastBar
    const endIdx = findWedgeTrendlineEndIdx(
        timestamps,
        H1,
        H2,
        L1,
        L3,
        extensionFrom,
        extensionTo,
    )
    if (endIdx <= L3.index) return null

    const upperAtEnd = linePriceAt(timestamps, H1, H2, endIdx)
    const lowerAtEnd = linePriceAt(timestamps, L1, L3, endIdx)
    if (upperAtEnd <= lowerAtEnd) return null

    const patternInProgress = !brokenDown
    const wedgeHeight = H1.price - L1.price
    const supportAtBreakout = brokenDown && breakoutIdx != null
        ? linePriceAt(timestamps, L1, L3, breakoutIdx)
        : lowerAtEnd

    const points: Point[] = [
        toPatternPoint('L1', timestamps[L1.index], L1.price),
        toPatternPoint('H1', timestamps[H1.index], H1.price),
        toPatternPoint('L2', timestamps[L2.index], L2.price),
        toPatternPoint('H2', timestamps[H2.index], H2.price),
        toPatternPoint('L3', timestamps[L3.index], L3.price),
        toPatternPoint('U', timestamps[endIdx], upperAtEnd),
        toPatternPoint('S', timestamps[endIdx], lowerAtEnd),
    ]

    if (brokenDown && breakoutIdx != null) {
        points.push(toPatternPoint('BR', timestamps[breakoutIdx], supportAtBreakout))
    } else {
        points.push(toPatternPoint('B', timestamps[endIdx], lowerAtEnd))
    }

    const priceLineSegments: ChartPattern['priceLineSegments'] = [
        { from: 'L1', to: 'H1', style: 'solid', color: wedgeColor },
        { from: 'H1', to: 'L2', style: 'solid', color: wedgeColor },
        { from: 'L2', to: 'H2', style: 'solid', color: wedgeColor },
        { from: 'H2', to: 'L3', style: 'solid', color: wedgeColor },
    ]
    if (!brokenDown) {
        priceLineSegments.push({ from: 'L3', to: 'B', style: 'dotted', color: wedgeColor })
    }

    return {
        kind: 'RISING_WEDGE',
        bias: 'bearish',
        points,
        priceLineSegments,
        polylineIds: brokenDown
            ? ['L1', 'H1', 'L2', 'H2', 'L3']
            : ['L1', 'H1', 'L2', 'H2', 'L3', 'B'],
        boundaryLines: [
            { from: 'H1', to: 'U', style: 'solid', color: wedgeColor },
            { from: 'L1', to: 'S', style: 'solid', color: wedgeColor },
        ],
        fillPolygonIds: ['H1', 'U', 'S', 'L1'],
        fillBackgroundColor: wedgeColor,
        fillBackgroundOpacity: 0.20,
        labels: [{
            pointId: 'H1',
            text: 'Rising Wedge',
            position: 'above',
            color: wedgeColor,
        }],
        priceTarget: includeTarget && brokenDown && breakoutIdx != null
            ? supportAtBreakout - wedgeHeight
            : undefined,
        targetAnchorPointId: brokenDown ? 'BR' : 'B',
        inProgress: patternInProgress,
    }
}

function buildFallingWedgePattern(
    timestamps: number[],
    H1: Pivot,
    L1: Pivot,
    H2: Pivot,
    L2: Pivot,
    H3: Pivot,
    high: number[],
    low: number[],
    options: {
        includeTarget: boolean
        inProgressSetting: boolean
        channelWidthRatioPct: number
        h2TolerancePct: number
        closes: number[]
        lastBar: number
        pivotLength: number
    },
): ChartPattern | null {
    const {
        includeTarget,
        inProgressSetting,
        channelWidthRatioPct,
        h2TolerancePct,
        closes,
        lastBar,
        pivotLength,
    } = options
    const wedgeColor = CHART_PATTERN_FALLING_WEDGE_COLOR

    if (H2.price >= H1.price * 1.002 || H3.price >= H2.price * 1.002) return null
    if (L2.price >= L1.price * 1.002) return null

    const highSlope = pivotSlope(H1, H3)
    const lowSlope = pivotSlope(L1, L2)
    if (highSlope >= 0 || lowSlope >= 0) return null
    if (highSlope >= lowSlope * 0.95) return null

    const lowerAtH1 = linePriceAt(timestamps, L1, L2, H1.index)
    const widthStart = H1.price - lowerAtH1
    if (widthStart <= 0) return null

    const lowerAtH3 = linePriceAt(timestamps, L1, L2, H3.index)
    const widthEnd = H3.price - lowerAtH3
    const minNarrowPct = Math.max(1, channelWidthRatioPct) / 100
    if (widthEnd >= widthStart * (1 - minNarrowPct)) return null
    if (H3.price <= lowerAtH3) return null

    if (!closesRespectWedgeBetween(closes, timestamps, H1, H3, L1, L2, H1.index, L2.index)) {
        return null
    }

    const upperAtH2 = linePriceAt(timestamps, H1, H3, H2.index)
    const channelAtH2 = upperAtH2 - linePriceAt(timestamps, L1, L2, H2.index)
    if (channelAtH2 <= 0) return null
    const h2Dev = (Math.abs(H2.price - upperAtH2) / channelAtH2) * 100
    if (h2Dev > h2TolerancePct) return null

    let breakoutIdx: number | undefined
    for (let j = H3.index + 1; j <= lastBar; j++) {
        const resistanceAtJ = linePriceAt(timestamps, H1, H3, j)
        if (closes[j] > resistanceAtJ) {
            breakoutIdx = j
            break
        }
    }

    if (!breakoutIdx && !inProgressSetting) return null

    const brokenUp = breakoutIdx != null
    const extensionFrom = H3.index + (brokenUp ? 0 : pivotLength)
    const extensionTo = brokenUp && breakoutIdx != null
        ? Math.max(breakoutIdx, H3.index + 1)
        : lastBar
    const endIdx = findWedgeTrendlineEndIdx(
        timestamps,
        H1,
        H3,
        L1,
        L2,
        extensionFrom,
        extensionTo,
    )
    if (endIdx <= H3.index) return null

    const upperAtEnd = linePriceAt(timestamps, H1, H3, endIdx)
    const lowerAtEnd = linePriceAt(timestamps, L1, L2, endIdx)
    if (upperAtEnd <= lowerAtEnd) return null

    const patternInProgress = !brokenUp
    const wedgeHeight = H1.price - L1.price
    const resistanceAtBreakout = brokenUp && breakoutIdx != null
        ? linePriceAt(timestamps, H1, H3, breakoutIdx)
        : upperAtEnd

    const points: Point[] = [
        toPatternPoint('H1', timestamps[H1.index], H1.price),
        toPatternPoint('L1', timestamps[L1.index], L1.price),
        toPatternPoint('H2', timestamps[H2.index], H2.price),
        toPatternPoint('L2', timestamps[L2.index], L2.price),
        toPatternPoint('H3', timestamps[H3.index], H3.price),
        toPatternPoint('U', timestamps[endIdx], upperAtEnd),
        toPatternPoint('S', timestamps[endIdx], lowerAtEnd),
    ]

    if (brokenUp && breakoutIdx != null) {
        points.push(toPatternPoint('BR', timestamps[breakoutIdx], resistanceAtBreakout))
    } else {
        points.push(toPatternPoint('B', timestamps[endIdx], upperAtEnd))
    }

    const priceLineSegments: ChartPattern['priceLineSegments'] = [
        { from: 'H1', to: 'L1', style: 'solid', color: wedgeColor },
        { from: 'L1', to: 'H2', style: 'solid', color: wedgeColor },
        { from: 'H2', to: 'L2', style: 'solid', color: wedgeColor },
        { from: 'L2', to: 'H3', style: 'solid', color: wedgeColor },
    ]
    if (!brokenUp) {
        priceLineSegments.push({ from: 'H3', to: 'B', style: 'dotted', color: wedgeColor })
    }

    return {
        kind: 'FALLING_WEDGE',
        bias: 'bullish',
        points,
        priceLineSegments,
        polylineIds: brokenUp
            ? ['H1', 'L1', 'H2', 'L2', 'H3']
            : ['H1', 'L1', 'H2', 'L2', 'H3', 'B'],
        boundaryLines: [
            { from: 'H1', to: 'U', style: 'solid', color: wedgeColor },
            { from: 'L1', to: 'S', style: 'solid', color: wedgeColor },
        ],
        fillPolygonIds: ['L1', 'H1', 'U', 'S'],
        fillBackgroundColor: wedgeColor,
        fillBackgroundOpacity: 0.20,
        labels: [{
            pointId: 'L1',
            text: 'Falling Wedge',
            position: 'below',
            color: wedgeColor,
        }],
        priceTarget: includeTarget && brokenUp && breakoutIdx != null
            ? resistanceAtBreakout + wedgeHeight
            : undefined,
        targetAnchorPointId: brokenUp ? 'BR' : 'B',
        inProgress: patternInProgress,
    }
}

function detectRisingWedgePentad(
    high: number[],
    low: number[],
    timestamps: number[],
    options: {
        channelWidthRatioPct: number
        l2TolerancePct: number
        includeTarget: boolean
        inProgressSetting: boolean
        closes: number[]
        lastBar: number
        pivotLength: number
        minBarsForPattern: number
        maxBarsForPattern: number
    },
): ChartPattern[] {
    const swings = buildAlternatingSwings(high, low, options.pivotLength)
    const patterns: ChartPattern[] = []

    for (let i = swings.length - 5; i >= 0; i--) {
        const pentad = swings.slice(i, i + 5)
        if (
            pentad[0].kind !== 'l'
            || pentad[1].kind !== 'h'
            || pentad[2].kind !== 'l'
            || pentad[3].kind !== 'h'
            || pentad[4].kind !== 'l'
        ) continue

        const L1 = pentad[0]
        const H1 = pentad[1]
        const L2 = pentad[2]
        const H2 = pentad[3]
        const L3 = pentad[4]

        if (L1.index >= H1.index || H1.index >= L2.index || L2.index >= H2.index || H2.index >= L3.index) continue

        const patternBars = L3.index - L1.index
        if (patternBars < options.minBarsForPattern || patternBars > options.maxBarsForPattern) continue

        const pattern = buildRisingWedgePattern(timestamps, L1, H1, L2, H2, L3, high, low, options)
        if (!pattern) continue

        patterns.push(pattern)
        return patterns
    }

    return patterns
}

function detectRisingWedgePivotPairs(
    high: number[],
    low: number[],
    timestamps: number[],
    options: {
        channelWidthRatioPct: number
        l2TolerancePct: number
        includeTarget: boolean
        inProgressSetting: boolean
        closes: number[]
        lastBar: number
        pivotLength: number
        minBarsForPattern: number
        maxBarsForPattern: number
    },
): ChartPattern[] {
    const pivotHighs = findPivotHighs(high, options.pivotLength, options.pivotLength)
    const pivotLows = findPivotLows(low, options.pivotLength, options.pivotLength)
    if (pivotHighs.length < 2 || pivotLows.length < 3) return []

    for (let j = pivotLows.length - 1; j >= 2; j--) {
        for (let i = j - 2; i >= Math.max(0, j - 6); i--) {
            const L1 = pivotLows[i]
            const L3 = pivotLows[j]
            const lowsBetween = pivotLows.filter((p) => p.index > L1.index && p.index < L3.index)
            const L2 = lowsBetween.length ? lowsBetween[lowsBetween.length - 1] : null
            if (!L2) continue

            const highsInRange = pivotHighs.filter((p) => p.index > L1.index && p.index < L3.index)
            if (highsInRange.length < 2) continue

            const H1 = highsInRange[0]
            const H2 = highsInRange[highsInRange.length - 1]

            const patternBars = L3.index - L1.index
            if (patternBars < options.minBarsForPattern || patternBars > options.maxBarsForPattern) continue

            const pattern = buildRisingWedgePattern(timestamps, L1, H1, L2, H2, L3, high, low, options)
            if (!pattern) continue

            return [pattern]
        }
    }

    return []
}

function detectFallingWedgePentad(
    high: number[],
    low: number[],
    timestamps: number[],
    options: {
        channelWidthRatioPct: number
        h2TolerancePct: number
        includeTarget: boolean
        inProgressSetting: boolean
        closes: number[]
        lastBar: number
        pivotLength: number
        minBarsForPattern: number
        maxBarsForPattern: number
    },
): ChartPattern[] {
    const swings = buildAlternatingSwings(high, low, options.pivotLength)
    const patterns: ChartPattern[] = []

    for (let i = swings.length - 5; i >= 0; i--) {
        const pentad = swings.slice(i, i + 5)
        if (
            pentad[0].kind !== 'h'
            || pentad[1].kind !== 'l'
            || pentad[2].kind !== 'h'
            || pentad[3].kind !== 'l'
            || pentad[4].kind !== 'h'
        ) continue

        const H1 = pentad[0]
        const L1 = pentad[1]
        const H2 = pentad[2]
        const L2 = pentad[3]
        const H3 = pentad[4]

        if (H1.index >= L1.index || L1.index >= H2.index || H2.index >= L2.index || L2.index >= H3.index) continue

        const patternBars = H3.index - H1.index
        if (patternBars < options.minBarsForPattern || patternBars > options.maxBarsForPattern) continue

        const pattern = buildFallingWedgePattern(timestamps, H1, L1, H2, L2, H3, high, low, options)
        if (!pattern) continue

        patterns.push(pattern)
        return patterns
    }

    return patterns
}

function detectFallingWedgePivotPairs(
    high: number[],
    low: number[],
    timestamps: number[],
    options: {
        channelWidthRatioPct: number
        h2TolerancePct: number
        includeTarget: boolean
        inProgressSetting: boolean
        closes: number[]
        lastBar: number
        pivotLength: number
        minBarsForPattern: number
        maxBarsForPattern: number
    },
): ChartPattern[] {
    const pivotHighs = findPivotHighs(high, options.pivotLength, options.pivotLength)
    const pivotLows = findPivotLows(low, options.pivotLength, options.pivotLength)
    if (pivotHighs.length < 3 || pivotLows.length < 2) return []

    for (let j = pivotHighs.length - 1; j >= 2; j--) {
        for (let i = j - 2; i >= Math.max(0, j - 6); i--) {
            const H1 = pivotHighs[i]
            const H3 = pivotHighs[j]
            const highsBetween = pivotHighs.filter((p) => p.index > H1.index && p.index < H3.index)
            const H2 = highsBetween.length ? highsBetween[highsBetween.length - 1] : null
            if (!H2) continue

            const lowsInRange = pivotLows.filter((p) => p.index > H1.index && p.index < H3.index)
            if (lowsInRange.length < 2) continue

            const L1 = lowsInRange[0]
            const L2 = lowsInRange[lowsInRange.length - 1]

            const patternBars = H3.index - H1.index
            if (patternBars < options.minBarsForPattern || patternBars > options.maxBarsForPattern) continue

            const pattern = buildFallingWedgePattern(timestamps, H1, L1, H2, L2, H3, high, low, options)
            if (!pattern) continue

            return [pattern]
        }
    }

    return []
}

function passesSecondPeakFilter(
    firstPrice: number,
    secondPrice: number,
    filter?: string,
): boolean {
    if (!filter || filter === 'Both') return true
    if (filter === 'Higher') return secondPrice > firstPrice
    if (filter === 'Lower') return secondPrice < firstPrice
    return true
}

function detectDoubleTop(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions
): ChartPattern[] {
    const {
        tolerancePct = 15,
        trendHeight = 50,
        priceTargetsFilter = 'All',
        patternsFilter = 'All',
        maxPatterns = 1,
        inProgress = true,
        secondTop,
        close,
    } = options
    const closes = close?.length === high.length
        ? close
        : high.map((h, i) => (h + low[i]) / 2)
    const pivotLength = resolveChartPatternPivotLength()
    const pivotHighs = findPivotHighs(high, pivotLength, pivotLength)
    const includeTarget = resolveIncludePriceTarget('DOUBLE_TOP', priceTargetsFilter)
    const inProgressSetting = resolveBooleanOption(inProgress, true)
    const lastBar = high.length - 1
    const swings = buildAlternatingSwings(high, low, pivotLength)
    const statusCandles = buildPatternStatusCandles(timestamps, high, low, closes)
    const candidates: DoubleTopCandidate[] = []
    const seenKeys = new Set<string>()
    const maxPairsToCheck = 40
    const pivotLows = findPivotLows(low, pivotLength, pivotLength)

    const tryAddCandidate = (
        point1: Pivot,
        top1: Pivot,
        trough: Pivot,
        top2: Pivot,
        formingSecondPeak: boolean,
    ) => {
        if (top1.index >= trough.index || trough.index >= top2.index) return
        const highsMidpoint = (top1.price + top2.price) / 2
        if (point1.price >= highsMidpoint) return
        const key = `${top1.index}-${trough.index}-${top2.index}`
        if (seenKeys.has(key)) return

        if (!extremumHeightsPassPermissibleDeviation(top1.price, top2.price, trough.price, tolerancePct, true)) return
        if (!passesSecondPeakFilter(top1.price, top2.price, secondTop)) return
        if (!passesDoubleTopTrendHeight(point1, top1, top2, trough.price, trendHeight)) return

        const point5 = findNecklineCloseBreakAfter(closes, trough.price, top2.index, true, inProgressSetting)
        if (!point5.broken && !inProgressSetting) return
        if (point5.index < 0) return

        const patternHeight = Math.max(top1.price, top2.price) - trough.price
        const pattern = buildDoubleTopPattern(timestamps, point1, top1, trough, top2, point5, {
            includeTarget,
            inProgressSetting,
            broken: point5.broken,
            formingSecondPeak,
        })
        seenKeys.add(key)
        candidates.push({
            pattern,
            score: scoreDoubleTopSimilarity(top1.price, top2.price, trough.price),
            top1Idx: top1.index,
            troughIdx: trough.index,
            top2Idx: top2.index,
            patternHeight,
            targetStatus: getDoubleTopTargetStatus(pattern, statusCandles),
        })
    }

    // TV: points 1–4 are 5/5 pivots — scan all top pairs with neckline trough between
    for (let j = pivotHighs.length - 1; j >= 1; j--) {
        for (let i = j - 1; i >= Math.max(0, j - maxPairsToCheck); i--) {
            const top1 = pivotHighs[i]
            const top2 = pivotHighs[j]
            const trough = findPivotLowBetween(pivotLows, top1.index, top2.index)
            if (!trough) continue
            const point1 = findDoubleTopPoint1(pivotLows, top1, trough, top2, low, high, pivotLength, trendHeight)
            tryAddCandidate(point1, top1, trough, top2, false)
        }
    }

    // TV In Progress: L-H-L with a forming second top (may not be a 5/5 pivot yet)
    if (inProgressSetting) {
        for (let i = 0; i + 2 < swings.length; i++) {
            if (swings[i].kind !== 'l' || swings[i + 1].kind !== 'h' || swings[i + 2].kind !== 'l') continue
            if (i + 3 < swings.length && swings[i + 3].kind === 'h') continue

            const point1 = swingToPivot(swings[i])
            const top1 = swingToPivot(swings[i + 1])
            const trough = swingToPivot(swings[i + 2])
            const formingTop = findHighestBarBetween(high, trough.index, lastBar)
            if (!formingTop || formingTop.index <= trough.index) continue

            const isConfirmedPivot = pivotHighs.some((p) => p.index === formingTop.index)
            const stillForming = !isConfirmedPivot || formingTop.index > lastBar - pivotLength
            if (!stillForming) continue

            tryAddCandidate(point1, top1, trough, formingTop, true)
        }
    }

    if (!candidates.length) return []

    const best = selectBestDoubleTopCandidate(candidates, patternsFilter)
    if (!best) return []

    return limitPatterns([best.pattern], maxPatterns, inProgressSetting)
}

function buildDoubleBottomPattern(
    timestamps: number[],
    point1: Pivot,
    bottom1: Pivot,
    peak: Pivot,
    bottom2: Pivot,
    point5: { index: number; price: number },
    options: {
        includeTarget: boolean
        inProgressSetting: boolean
        broken: boolean
        formingSecondBottom: boolean
    },
): ChartPattern {
    const height = peak.price - Math.min(bottom1.price, bottom2.price)
    const patternInProgress = options.inProgressSetting && !options.broken
    const dottedFromSecondBottom = options.formingSecondBottom && !options.broken

    return {
        kind: 'DOUBLE_BOTTOM',
        bias: 'bullish',
        points: [
            toPatternPoint('1', timestamps[point1.index], point1.price),
            toPatternPoint('2', timestamps[bottom1.index], bottom1.price),
            toPatternPoint('3', timestamps[peak.index], peak.price),
            toPatternPoint('4', timestamps[bottom2.index], bottom2.price),
            toPatternPoint('5', timestamps[point5.index], point5.price),
        ],
        priceLineSegments: [
            { from: '1', to: '2', style: 'solid', color: TV_DOUBLE_BOTTOM_COLOR },
            { from: '2', to: '3', style: 'solid', color: TV_DOUBLE_BOTTOM_COLOR },
            { from: '3', to: '4', style: dottedFromSecondBottom ? 'dotted' : 'solid', color: TV_DOUBLE_BOTTOM_COLOR },
            { from: '4', to: '5', style: patternInProgress ? 'dotted' : 'solid', color: TV_DOUBLE_BOTTOM_COLOR },
        ],
        polylineIds: ['1', '2', '3', '4', '5'],
        boundaryLines: [{ from: '3', to: '5', style: 'dotted', color: TV_DOUBLE_BOTTOM_COLOR }],
        fillTriangles: undefined,
        fillBackgroundColor: TV_DOUBLE_BOTTOM_COLOR,
        fillBackgroundOpacity: TV_DOUBLE_PATTERN_FILL_OPACITY,
        labels: tvDoubleBottomPeakLabels(),
        priceTarget: options.includeTarget ? peak.price + height : undefined,
        targetAnchorPointId: '5',
        inProgress: patternInProgress,
    }
}

function passesDoubleBottomTrendHeight(
    point1: Pivot,
    bottom1: Pivot,
    bottom2: Pivot,
    neckPrice: number,
    trendHeightPct: number,
): boolean {
    const patternHeight = neckPrice - Math.min(bottom1.price, bottom2.price)
    const precedingTrend = point1.price - bottom1.price
    if (patternHeight <= 0 || precedingTrend <= 0) return false
    return (precedingTrend / patternHeight) * 100 >= trendHeightPct
}

function detectDoubleBottom(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions
): ChartPattern[] {
    const {
        tolerancePct = 15,
        trendHeight = 50,
        priceTargetsFilter = 'All',
        patternsFilter = 'All',
        maxPatterns = 1,
        inProgress = true,
        secondBottom,
        close,
    } = options
    const closes = close?.length === high.length
        ? close
        : high.map((h, i) => (h + low[i]) / 2)
    const pivotLength = resolveChartPatternPivotLength()
    const pivotHighs = findPivotHighs(high, pivotLength, pivotLength)
    const pivotLows = findPivotLows(low, pivotLength, pivotLength)
    const includeTarget = resolveIncludePriceTarget('DOUBLE_BOTTOM', priceTargetsFilter)
    const inProgressSetting = resolveBooleanOption(inProgress, true)
    const lastBar = high.length - 1
    const swings = buildAlternatingSwings(high, low, pivotLength)
    const statusCandles = buildPatternStatusCandles(timestamps, high, low, closes)
    const candidates: DoubleTopCandidate[] = []
    const seenKeys = new Set<string>()
    const maxPairsToCheck = 40

    const tryAddCandidate = (
        point1: Pivot,
        bottom1: Pivot,
        peak: Pivot,
        bottom2: Pivot,
        formingSecondBottom: boolean,
    ) => {
        if (bottom1.index >= peak.index || peak.index >= bottom2.index) return
        const lowsMidpoint = (bottom1.price + bottom2.price) / 2
        if (point1.price <= lowsMidpoint) return
        const key = `${bottom1.index}-${peak.index}-${bottom2.index}`
        if (seenKeys.has(key)) return

        if (!extremumHeightsPassPermissibleDeviation(bottom1.price, bottom2.price, peak.price, tolerancePct, false)) return
        if (!passesSecondPeakFilter(bottom1.price, bottom2.price, secondBottom)) return
        if (!passesDoubleBottomTrendHeight(point1, bottom1, bottom2, peak.price, trendHeight)) return

        const point5 = findNecklineCloseBreakAfter(closes, peak.price, bottom2.index, false, inProgressSetting)
        if (!point5.broken && !inProgressSetting) return
        if (point5.index < 0) return

        const patternHeight = peak.price - Math.min(bottom1.price, bottom2.price)
        const pattern = buildDoubleBottomPattern(timestamps, point1, bottom1, peak, bottom2, point5, {
            includeTarget,
            inProgressSetting,
            broken: point5.broken,
            formingSecondBottom,
        })
        seenKeys.add(key)
        candidates.push({
            pattern,
            score: scoreDoubleTopSimilarity(bottom1.price, bottom2.price, peak.price),
            top1Idx: bottom1.index,
            troughIdx: peak.index,
            top2Idx: bottom2.index,
            patternHeight,
            targetStatus: getDoubleBottomTargetStatus(pattern, statusCandles),
        })
    }

    // TV: points 1–4 are 5/5 pivots — scan all bottom pairs with neckline peak between
    for (let j = pivotLows.length - 1; j >= 1; j--) {
        for (let i = j - 1; i >= Math.max(0, j - maxPairsToCheck); i--) {
            const bottom1 = pivotLows[i]
            const bottom2 = pivotLows[j]
            const peak = findPivotHighBetween(pivotHighs, bottom1.index, bottom2.index)
            if (!peak) continue
            const point1 = findDoubleBottomPoint1(pivotHighs, bottom1, peak, bottom2, low, high, pivotLength, trendHeight)
            if (!point1) continue
            tryAddCandidate(point1, bottom1, peak, bottom2, false)
        }
    }

    // TV In Progress: H-L-H with forming second bottom (may not be 5/5 pivot yet)
    if (inProgressSetting) {
        for (let i = 0; i + 2 < swings.length; i++) {
            if (swings[i].kind !== 'h' || swings[i + 1].kind !== 'l' || swings[i + 2].kind !== 'h') continue
            if (i + 3 < swings.length && swings[i + 3].kind === 'l') continue

            const point1 = swingToPivot(swings[i])
            const bottom1 = swingToPivot(swings[i + 1])
            const peak = swingToPivot(swings[i + 2])
            const formingBottom = findLowestBarBetween(low, peak.index, lastBar)
            if (!formingBottom || formingBottom.index <= peak.index) continue

            const isConfirmedPivot = pivotLows.some((p) => p.index === formingBottom.index)
            const stillForming = !isConfirmedPivot || formingBottom.index > lastBar - pivotLength
            if (!stillForming) continue

            tryAddCandidate(point1, bottom1, peak, formingBottom, true)
        }
    }

    if (!candidates.length) return []

    const best = selectBestDoubleTopCandidate(candidates, patternsFilter)
    if (!best) return []

    return limitPatterns([best.pattern], maxPatterns, inProgressSetting)
}

function detectAscendingTriangle(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions,
): ChartPattern[] {
    const {
        priceTargetsFilter = 'All',
        maxPatterns = 1,
        inProgress = true,
        close,
        swingLength = ASCENDING_TRIANGLE_DEFAULTS.swingLength,
        minBarsForPattern = ASCENDING_TRIANGLE_DEFAULTS.minBarsForPattern,
        maxBarsForPattern = ASCENDING_TRIANGLE_DEFAULTS.maxBarsForPattern,
        maxBarsForConfirmation = ASCENDING_TRIANGLE_DEFAULTS.maxBarsForConfirmation,
        atrLength = ASCENDING_TRIANGLE_DEFAULTS.atrLength,
        minSwingProminenceAtr = ASCENDING_TRIANGLE_DEFAULTS.minSwingProminenceAtr,
        minTriangleHeightAtr = ASCENDING_TRIANGLE_DEFAULTS.minTriangleHeightAtr,
        maxTriangleHeightAtr = ASCENDING_TRIANGLE_DEFAULTS.maxTriangleHeightAtr,
        maxHorizontalToleranceAtr = ASCENDING_TRIANGLE_DEFAULTS.maxHorizontalToleranceAtr,
    } = options

    const closes = close?.length === high.length
        ? close
        : high.map((h, i) => (h + low[i]) / 2)

    const atrValues = computeAtr(high, low, closes, atrLength, high.length)
    const pivotLength = Math.max(2, swingLength)
    const swings = buildAlternatingSwings(high, low, pivotLength)
    const includeTarget = resolveIncludePriceTarget('ASCENDING_TRIANGLE', priceTargetsFilter)
    const patterns: ChartPattern[] = []
    const resistanceColor = '#F23645'
    const supportColor = '#089981'
    const zigZagColor = '#F5A400'
    const fillColor = '#089981'
    const lastBar = high.length - 1

    for (let i = swings.length - 4; i >= 0; i--) {
        const quad = swings.slice(i, i + 4)
        if (quad[0].kind !== 'h' || quad[1].kind !== 'l' || quad[2].kind !== 'h' || quad[3].kind !== 'l') continue
        const H1 = quad[0]
        const L1 = quad[1]
        const H2 = quad[2]
        const L2 = quad[3]

        if (H1.index >= L1.index || L1.index >= H2.index || H2.index >= L2.index) continue

        const patternBars = L2.index - H1.index
        if (patternBars < minBarsForPattern || patternBars > maxBarsForPattern) continue
        if (L2.price <= L1.price) continue

        const resistance = Math.max(H1.price, H2.price)
        const atrAtPattern = atrAtBar(atrValues, H2.index, atrLength)
        if (!Number.isFinite(atrAtPattern) || atrAtPattern <= 0) continue

        const horizontalDiff = Math.abs(H1.price - H2.price)
        if (horizontalDiff > maxHorizontalToleranceAtr * atrAtPattern) continue

        const triangleHeight = resistance - L1.price
        if (triangleHeight < minTriangleHeightAtr * atrAtPattern) continue
        if (triangleHeight > maxTriangleHeightAtr * atrAtPattern) continue

        if (!swingHighProminence(high, low, H1.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, H1.index, atrLength))) continue
        if (!swingLowProminence(high, low, L1.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, L1.index, atrLength))) continue
        if (!swingHighProminence(high, low, H2.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, H2.index, atrLength))) continue
        if (!swingLowProminence(high, low, L2.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, L2.index, atrLength))) continue

        const lowSlope = pivotSlope(L1, L2)
        const highSlope = pivotSlope(H1, H2)
        if (lowSlope <= 0) continue
        if (!slopeIsNearHorizontal(highSlope, resistance, 0.35)) continue

        const confirmationEnd = Math.min(lastBar, L2.index + maxBarsForConfirmation)
        let breakoutIdx: number | undefined
        for (let j = L2.index + 1; j <= confirmationEnd; j++) {
            if (closes[j] > resistance) {
                breakoutIdx = j
                break
            }
        }

        const pastConfirmationWindow = lastBar > L2.index + maxBarsForConfirmation
        if (!breakoutIdx && pastConfirmationWindow) continue
        if (!breakoutIdx && !inProgress) continue

        const endIdx = breakoutIdx ?? Math.min(lastBar, L2.index + pivotLength)
        const supportAtEnd = linePriceAt(timestamps, L1, L2, endIdx)
        if (supportAtEnd >= resistance) continue

        const brokenUp = breakoutIdx != null
        let peakIdx = endIdx
        if (brokenUp && breakoutIdx != null) {
            const searchEnd = Math.min(high.length - 1, breakoutIdx + pivotLength * 2)
            for (let j = breakoutIdx; j <= searchEnd; j++) {
                if (high[j] >= high[peakIdx]) peakIdx = j
            }
        }

        const patternInProgress = !brokenUp
        const breakoutPeakPrice = brokenUp ? high[peakIdx] : supportAtEnd
        const intersectionIdx = brokenUp ? breakoutIdx! : endIdx

        patterns.push({
            kind: 'ASCENDING_TRIANGLE',
            bias: 'bullish',
            points: [
                toPatternPoint('H1', timestamps[H1.index], resistance),
                toPatternPoint('L1', timestamps[L1.index], L1.price),
                toPatternPoint('H2', timestamps[H2.index], resistance),
                toPatternPoint('L2', timestamps[L2.index], L2.price),
                toPatternPoint('B', timestamps[peakIdx], breakoutPeakPrice),
                toPatternPoint('R', timestamps[intersectionIdx], resistance),
                toPatternPoint('S', timestamps[endIdx], supportAtEnd),
            ],
            priceLineSegments: [
                { from: 'H1', to: 'L1', style: 'dotted', color: zigZagColor },
                { from: 'L1', to: 'H2', style: 'dotted', color: zigZagColor },
                { from: 'H2', to: 'L2', style: 'dotted', color: zigZagColor },
                { from: 'L2', to: 'B', style: patternInProgress ? 'dotted' : 'solid', color: supportColor, width: 2 },
            ],
            polylineIds: ['H1', 'L1', 'H2', 'L2', 'B'],
            boundaryLines: [
                { from: 'H1', to: 'R', style: 'solid', color: resistanceColor },
                { from: 'L1', to: 'S', style: 'solid', color: supportColor },
            ],
            fillPolygonIds: ['H1', 'R', 'S', 'L1'],
            fillBackgroundColor: fillColor,
            fillBackgroundOpacity: 0.18,
            labels: [
                { pointId: 'H1', text: 'H1', color: resistanceColor },
                { pointId: 'H2', text: 'H2', color: resistanceColor },
                { pointId: 'L1', text: 'L1', position: 'below', color: supportColor },
                { pointId: 'L2', text: 'L2', position: 'below', color: supportColor },
            ],
            breakoutMarker: brokenUp ? { pointId: 'B', text: '✓', color: supportColor, position: 'above' } : undefined,
            lineEndMarkers: brokenUp ? [{ pointId: 'R', direction: 'up', color: supportColor }] : undefined,
            priceTarget: includeTarget && brokenUp ? resistance + triangleHeight : undefined,
            targetAnchorPointId: 'B',
            inProgress: patternInProgress,
        })
        return limitPatterns(patterns, maxPatterns, inProgress)
    }

    return limitPatterns(patterns, maxPatterns, inProgress)
}

function detectDescendingTriangle(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions,
): ChartPattern[] {
    const {
        priceTargetsFilter = 'All',
        maxPatterns = 1,
        inProgress = true,
        close,
        swingLength = TRIANGLE_PATTERN_DEFAULTS.swingLength,
        minBarsForPattern = TRIANGLE_PATTERN_DEFAULTS.minBarsForPattern,
        maxBarsForPattern = TRIANGLE_PATTERN_DEFAULTS.maxBarsForPattern,
        maxBarsForConfirmation = TRIANGLE_PATTERN_DEFAULTS.maxBarsForConfirmation,
        atrLength = TRIANGLE_PATTERN_DEFAULTS.atrLength,
        minSwingProminenceAtr = TRIANGLE_PATTERN_DEFAULTS.minSwingProminenceAtr,
        minTriangleHeightAtr = TRIANGLE_PATTERN_DEFAULTS.minTriangleHeightAtr,
        maxTriangleHeightAtr = TRIANGLE_PATTERN_DEFAULTS.maxTriangleHeightAtr,
        maxHorizontalToleranceAtr = TRIANGLE_PATTERN_DEFAULTS.maxHorizontalToleranceAtr,
    } = options

    const closes = close?.length === high.length
        ? close
        : high.map((h, i) => (h + low[i]) / 2)

    const atrValues = computeAtr(high, low, closes, atrLength, high.length)
    const pivotLength = Math.max(2, swingLength)
    const swings = buildAlternatingSwings(high, low, pivotLength)
    const includeTarget = resolveIncludePriceTarget('DESCENDING_TRIANGLE', priceTargetsFilter)
    const patterns: ChartPattern[] = []
    const resistanceColor = '#F23645'
    const supportColor = '#089981'
    const zigZagColor = '#F5A400'
    const fillColor = '#F23645'
    const lastBar = high.length - 1

    for (let i = swings.length - 4; i >= 0; i--) {
        const quad = swings.slice(i, i + 4)
        if (quad[0].kind !== 'l' || quad[1].kind !== 'h' || quad[2].kind !== 'l' || quad[3].kind !== 'h') continue
        const L1 = quad[0]
        const H1 = quad[1]
        const L2 = quad[2]
        const H2 = quad[3]

        if (L1.index >= H1.index || H1.index >= L2.index || L2.index >= H2.index) continue

        const patternBars = H2.index - L1.index
        if (patternBars < minBarsForPattern || patternBars > maxBarsForPattern) continue
        if (H2.price >= H1.price) continue

        const support = Math.min(L1.price, L2.price)
        const atrAtPattern = atrAtBar(atrValues, H2.index, atrLength)
        if (!Number.isFinite(atrAtPattern) || atrAtPattern <= 0) continue

        const horizontalDiff = Math.abs(L1.price - L2.price)
        if (horizontalDiff > maxHorizontalToleranceAtr * atrAtPattern) continue

        const triangleHeight = H1.price - support
        if (triangleHeight < minTriangleHeightAtr * atrAtPattern) continue
        if (triangleHeight > maxTriangleHeightAtr * atrAtPattern) continue

        if (!swingLowProminence(high, low, L1.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, L1.index, atrLength))) continue
        if (!swingHighProminence(high, low, H1.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, H1.index, atrLength))) continue
        if (!swingLowProminence(high, low, L2.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, L2.index, atrLength))) continue
        if (!swingHighProminence(high, low, H2.index, pivotLength, minSwingProminenceAtr, atrAtBar(atrValues, H2.index, atrLength))) continue

        const highSlope = pivotSlope(H1, H2)
        const lowSlope = pivotSlope(L1, L2)
        if (highSlope >= 0) continue
        if (!slopeIsNearHorizontal(lowSlope, support, 0.35)) continue

        const confirmationEnd = Math.min(lastBar, H2.index + maxBarsForConfirmation)
        let breakoutIdx: number | undefined
        for (let j = H2.index + 1; j <= confirmationEnd; j++) {
            if (closes[j] < support) {
                breakoutIdx = j
                break
            }
        }

        const pastConfirmationWindow = lastBar > H2.index + maxBarsForConfirmation
        if (!breakoutIdx && pastConfirmationWindow) continue
        if (!breakoutIdx && !inProgress) continue

        const endIdx = breakoutIdx ?? Math.min(lastBar, H2.index + pivotLength)
        const resistanceAtEnd = linePriceAt(timestamps, H1, H2, endIdx)
        if (resistanceAtEnd <= support) continue

        const brokenDown = breakoutIdx != null
        let troughIdx = endIdx
        if (brokenDown && breakoutIdx != null) {
            const searchEnd = Math.min(low.length - 1, breakoutIdx + pivotLength * 2)
            for (let j = breakoutIdx; j <= searchEnd; j++) {
                if (low[j] <= low[troughIdx]) troughIdx = j
            }
        }

        const patternInProgress = !brokenDown
        const breakoutTroughPrice = brokenDown ? low[troughIdx] : resistanceAtEnd
        const intersectionIdx = brokenDown ? breakoutIdx! : endIdx

        patterns.push({
            kind: 'DESCENDING_TRIANGLE',
            bias: 'bearish',
            points: [
                toPatternPoint('L1', timestamps[L1.index], support),
                toPatternPoint('H1', timestamps[H1.index], H1.price),
                toPatternPoint('L2', timestamps[L2.index], support),
                toPatternPoint('H2', timestamps[H2.index], H2.price),
                toPatternPoint('B', timestamps[troughIdx], breakoutTroughPrice),
                toPatternPoint('S', timestamps[intersectionIdx], support),
                toPatternPoint('R', timestamps[endIdx], resistanceAtEnd),
            ],
            priceLineSegments: [
                { from: 'L1', to: 'H1', style: 'dotted', color: zigZagColor },
                { from: 'H1', to: 'L2', style: 'dotted', color: zigZagColor },
                { from: 'L2', to: 'H2', style: 'dotted', color: zigZagColor },
                { from: 'H2', to: 'B', style: patternInProgress ? 'dotted' : 'solid', color: resistanceColor, width: 2 },
            ],
            polylineIds: ['L1', 'H1', 'L2', 'H2', 'B'],
            boundaryLines: [
                { from: 'L1', to: 'S', style: 'solid', color: supportColor },
                { from: 'H1', to: 'R', style: 'solid', color: resistanceColor },
            ],
            fillPolygonIds: ['L1', 'H1', 'R', 'S'],
            fillBackgroundColor: fillColor,
            fillBackgroundOpacity: 0.18,
            labels: [
                { pointId: 'L1', text: 'L1', position: 'below', color: supportColor },
                { pointId: 'L2', text: 'L2', position: 'below', color: supportColor },
                { pointId: 'H1', text: 'H1', color: resistanceColor },
                { pointId: 'H2', text: 'H2', color: resistanceColor },
            ],
            breakoutMarker: brokenDown ? { pointId: 'B', text: '✓', color: resistanceColor, position: 'below' } : undefined,
            lineEndMarkers: brokenDown ? [{ pointId: 'R', direction: 'down', color: resistanceColor }] : undefined,
            priceTarget: includeTarget && brokenDown ? support - triangleHeight : undefined,
            targetAnchorPointId: 'B',
            inProgress: patternInProgress,
        })
        return limitPatterns(patterns, maxPatterns, inProgress)
    }

    return limitPatterns(patterns, maxPatterns, inProgress)
}

function detectRisingWedge(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions,
): ChartPattern[] {
    const {
        tolerancePct = 15,
        channelWidthRatioPct = 10,
        priceTargetsFilter = 'All',
        maxPatterns = 1,
        inProgress = true,
        close,
    } = options

    const closes = close?.length === high.length
        ? close
        : high.map((h, i) => (h + low[i]) / 2)

    const includeTarget = resolveIncludePriceTarget('RISING_WEDGE', priceTargetsFilter)
    const inProgressSetting = resolveBooleanOption(inProgress, true)
    const lastBar = high.length - 1
    const sharedOptions = {
        channelWidthRatioPct,
        l2TolerancePct: tolerancePct,
        includeTarget,
        inProgressSetting,
        closes,
        lastBar,
        pivotLength: TV_PIVOT,
        minBarsForPattern: 6,
        maxBarsForPattern: 150,
    }

    const pentadPatterns = detectRisingWedgePentad(high, low, timestamps, sharedOptions)
    if (pentadPatterns.length) {
        return limitPatterns(pentadPatterns, maxPatterns, inProgressSetting)
    }

    const pivotPairPatterns = detectRisingWedgePivotPairs(high, low, timestamps, sharedOptions)
    return limitPatterns(pivotPairPatterns, maxPatterns, inProgressSetting)
}

function detectFallingWedge(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions,
): ChartPattern[] {
    const {
        tolerancePct = 15,
        channelWidthRatioPct = 10,
        priceTargetsFilter = 'All',
        maxPatterns = 1,
        inProgress = true,
        close,
    } = options

    const closes = close?.length === high.length
        ? close
        : high.map((h, i) => (h + low[i]) / 2)

    const includeTarget = resolveIncludePriceTarget('FALLING_WEDGE', priceTargetsFilter)
    const inProgressSetting = resolveBooleanOption(inProgress, true)
    const lastBar = high.length - 1
    const sharedOptions = {
        channelWidthRatioPct,
        h2TolerancePct: tolerancePct,
        includeTarget,
        inProgressSetting,
        closes,
        lastBar,
        pivotLength: TV_PIVOT,
        minBarsForPattern: 6,
        maxBarsForPattern: 150,
    }

    const pentadPatterns = detectFallingWedgePentad(high, low, timestamps, sharedOptions)
    if (pentadPatterns.length) {
        return limitPatterns(pentadPatterns, maxPatterns, inProgressSetting)
    }

    const pivotPairPatterns = detectFallingWedgePivotPairs(high, low, timestamps, sharedOptions)
    return limitPatterns(pivotPairPatterns, maxPatterns, inProgressSetting)
}

type PennantCandidate = {
    pattern: ChartPattern
    span: number
    targetStatus: ChartPatternTargetStatus
}

function filterPennantCandidatesByPatternsFilter(
    candidates: PennantCandidate[],
    patternsFilter: string,
): PennantCandidate[] {
    if (patternsFilter === 'Only Awaiting') {
        return candidates.filter((c) => isAwaitingPatternStatus(c.targetStatus))
    }
    if (patternsFilter === 'Awaiting and Reached') {
        return candidates.filter((c) => c.targetStatus !== 'Failed')
    }
    if (patternsFilter === 'Last Awaiting') {
        const awaiting = candidates
            .filter((c) => isAwaitingPatternStatus(c.targetStatus))
            .sort((a, b) => b.span - a.span)
        return awaiting.length ? [awaiting[0]] : []
    }
    return candidates
}

function selectBestPennantCandidate(
    candidates: PennantCandidate[],
    patternsFilter: string,
): PennantCandidate | null {
    const filtered = filterPennantCandidatesByPatternsFilter(candidates, patternsFilter)
    if (!filtered.length) return null

    return [...filtered].sort((a, b) => {
        const aAwaiting = isAwaitingPatternStatus(a.targetStatus) ? 0 : 1
        const bAwaiting = isAwaitingPatternStatus(b.targetStatus) ? 0 : 1
        if (aAwaiting !== bAwaiting) return aAwaiting - bAwaiting
        return b.span - a.span
    })[0]
}

function collectPennantSwingPairs(
    poleEndIdx: number,
    poleEndPrice: number,
    maxConsBars: number,
    pivotHighs: Pivot[],
    pivotLows: Pivot[],
    bearish: boolean,
): Array<{ swingHigh: Pivot; swingLow: Pivot }> {
    const endWindow = poleEndIdx + maxConsBars
    const highsInWindow = pivotHighs.filter((p) => p.index > poleEndIdx && p.index <= endWindow)
    const lowsInWindow = pivotLows.filter((p) => p.index > poleEndIdx && p.index <= endWindow)
    const pairs: Array<{ swingHigh: Pivot; swingLow: Pivot }> = []

    if (bearish) {
        for (const swingHigh of highsInWindow) {
            const swingLow = lowsInWindow.find((p) => p.index > swingHigh.index)
            if (swingLow) pairs.push({ swingHigh, swingLow })
        }
    } else {
        for (const swingLow of lowsInWindow) {
            const swingHigh = highsInWindow.find((p) => p.index > swingLow.index && p.price < poleEndPrice)
            if (swingHigh) pairs.push({ swingHigh, swingLow })
        }
    }

    return pairs
}

function detectPennant(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions,
    kind: 'BEARISH_PENNANT' | 'BULLISH_PENNANT',
): ChartPattern[] {
    const {
        priceTargetsFilter = 'All',
        maxPatterns = 1,
        inProgress = true,
        maxRollbackPct = 50,
        tolerancePct = 40,
        patternsFilter = 'All',
        close,
    } = options
    const bearish = kind === 'BEARISH_PENNANT'
    const pennantColor = bearish ? CHART_PATTERN_BEARISH_PENNANT_COLOR : CHART_PATTERN_BULLISH_PENNANT_COLOR
    const patternTitle = bearish ? 'Bearish Pennant' : 'Bullish Pennant'
    const inProgressSetting = resolveBooleanOption(inProgress, true)
    const pivotLength = resolveChartPatternPivotLength()
    const includeTarget = resolveIncludePriceTarget(kind, priceTargetsFilter)
    const minPoleBars = pivotLength
    const maxPoleBars = pivotLength * 12
    const maxConsBars = pivotLength * 12
    const minPolePct = 0.012
    const lastBar = high.length - 1
    const pivotHighs = findPivotHighs(high, pivotLength, pivotLength)
    const pivotLows = findPivotLows(low, pivotLength, pivotLength)
    const scanStart = Math.max(0, high.length - TV_PIVOT * 20)
    const candidates: PennantCandidate[] = []

    const closes = close?.length === high.length
        ? close
        : high.map((h, idx) => (h + low[idx]) / 2)

    const statusCandles = buildPatternStatusCandles(timestamps, high, low, closes)

    const tryAddPattern = (
        poleStart: Pivot,
        poleEnd: Pivot,
        swingHigh: Pivot,
        swingLow: Pivot,
    ) => {
        const poleStartPrice = poleStart.price
        const poleEndPrice = poleEnd.price
        const poleAbs = bearish ? poleStartPrice - poleEndPrice : poleEndPrice - poleStartPrice
        if (poleAbs <= 0) return
        if (poleAbs / poleEndPrice < minPolePct) return

        const consEndIdx = Math.min(
            lastBar,
            Math.max(swingHigh.index, swingLow.index) + pivotLength,
            poleEnd.index + maxConsBars,
        )
        if (consEndIdx <= poleEnd.index + 1) return

        const channelStart = swingHigh.price - swingLow.price
        if (channelStart <= 0) return
        if (channelStart >= poleAbs * 0.75) return

        const apexIdx = Math.min(lastBar, consEndIdx + pivotLength * 2)
        const apexPrice = (swingHigh.price + swingLow.price) / 2
        const upperAtEnd = linePriceAt(
            timestamps,
            swingHigh,
            { index: apexIdx, price: apexPrice },
            consEndIdx,
        )
        const lowerAtEnd = bearish
            ? linePriceAt(timestamps, swingLow, { index: apexIdx, price: apexPrice }, consEndIdx)
            : swingLow.price
        if (upperAtEnd <= lowerAtEnd) return

        const widthStart = channelStart
        const widthEnd = upperAtEnd - lowerAtEnd
        if (widthEnd <= 0 || widthEnd >= widthStart * 0.9) return

        const upperSlope = pivotSlope(swingHigh, { index: consEndIdx, price: upperAtEnd })
        const lowerSlope = bearish
            ? pivotSlope(swingLow, { index: consEndIdx, price: lowerAtEnd })
            : 0
        const priceScale = (swingHigh.price + swingLow.price) / 2

        if (upperSlope >= -0.000001) return
        if (bearish) {
            if (lowerSlope <= 0.000001) return
            if (!slopesAreSymmetric(upperSlope, lowerSlope, priceScale, tolerancePct)) return
        } else if (!slopeIsNearHorizontal(lowerSlope, priceScale, 1.2)) {
            return
        }

        const rollbackPct = bearish
            ? ((swingHigh.price - poleEndPrice) / poleAbs) * 100
            : ((poleEndPrice - swingHigh.price) / poleAbs) * 100
        if (rollbackPct < 0 || rollbackPct > maxRollbackPct) return

        let breakoutIdx: number | undefined
        for (let j = consEndIdx + 1; j <= lastBar; j++) {
            const lowerAtJ = linePriceAt(timestamps, swingLow, { index: consEndIdx, price: lowerAtEnd }, j)
            const upperAtJ = linePriceAt(timestamps, swingHigh, { index: consEndIdx, price: upperAtEnd }, j)
            if (bearish && closes[j] < lowerAtJ) {
                breakoutIdx = j
                break
            }
            if (!bearish && closes[j] > upperAtJ) {
                breakoutIdx = j
                break
            }
        }

        const brokenOut = breakoutIdx != null
        const patternInProgress = consEndIdx >= lastBar - pivotLength
        if (!brokenOut && patternInProgress && !inProgressSetting) return

        const segmentStyle = patternInProgress && !brokenOut ? 'dotted' as const : 'solid' as const
        const target = bearish ? lowerAtEnd - poleAbs : poleEndPrice + poleAbs

        const patternPoints: Point[] = [
            toPatternPoint('1', timestamps[poleStart.index], poleStartPrice),
            toPatternPoint('2', timestamps[poleEnd.index], poleEndPrice),
            toPatternPoint('3', timestamps[swingHigh.index], swingHigh.price),
            toPatternPoint('4', timestamps[swingLow.index], swingLow.price),
            toPatternPoint('5', timestamps[consEndIdx], upperAtEnd),
            toPatternPoint('6', timestamps[consEndIdx], lowerAtEnd),
        ]

        let targetAnchorPointId = bearish ? '6' : '2'
        if (brokenOut && breakoutIdx != null) {
            const breakoutPrice = bearish ? low[breakoutIdx] : high[breakoutIdx]
            patternPoints.push(toPatternPoint('B', timestamps[breakoutIdx], breakoutPrice))
            targetAnchorPointId = 'B'
        }

        const priceLineSegments = bearish
            ? [
                { from: '1', to: '2', style: 'solid' as const, color: pennantColor },
                { from: '2', to: '3', style: segmentStyle, color: pennantColor },
                { from: '3', to: '4', style: segmentStyle, color: pennantColor },
                ...(brokenOut ? [{ from: '4', to: 'B', style: 'solid' as const, color: pennantColor }] : []),
            ]
            : [
                { from: '1', to: '2', style: 'solid' as const, color: pennantColor },
                { from: '2', to: '4', style: segmentStyle, color: pennantColor },
                { from: '4', to: '3', style: segmentStyle, color: pennantColor },
                ...(brokenOut ? [{ from: '3', to: 'B', style: 'solid' as const, color: pennantColor }] : []),
            ]

        const pattern: ChartPattern = {
            kind,
            bias: bearish ? 'bearish' : 'bullish',
            points: patternPoints,
            priceLineSegments,
            polylineIds: bearish
                ? (brokenOut ? ['1', '2', '3', '4', 'B'] : ['1', '2', '3', '4'])
                : (brokenOut ? ['1', '2', '4', '3', 'B'] : ['1', '2', '4', '3']),
            boundaryLines: [
                { from: '3', to: '5', style: 'solid', color: pennantColor },
                { from: '4', to: '6', style: 'solid', color: pennantColor },
            ],
            fillTriangles: [['3', '4', '6'], ['3', '5', '6']],
            fillBackgroundColor: pennantColor,
            fillBackgroundOpacity: 0.18,
            labels: [{
                pointId: bearish ? '4' : '2',
                text: patternTitle,
                position: bearish ? 'below' : 'above',
                color: pennantColor,
            }],
            priceTarget: includeTarget ? target : undefined,
            targetAnchorPointId,
            inProgress: patternInProgress && !brokenOut,
        }

        const span = consEndIdx - poleStart.index
        const targetStatus = getChartPatternTargetStatus(pattern, statusCandles)
        const existingIdx = candidates.findIndex((candidate) => {
            const existingStart = candidate.pattern.points.find((p) => p.id === '1')
            const existingEnd = candidate.pattern.points.find((p) => p.id === '2')
            const newStart = pattern.points.find((p) => p.id === '1')
            const newEnd = pattern.points.find((p) => p.id === '2')
            return existingStart?.x === newStart?.x && existingEnd?.x === newEnd?.x
        })
        if (existingIdx >= 0) {
            if (span > candidates[existingIdx].span) {
                candidates[existingIdx] = { pattern, span, targetStatus }
            }
            return
        }
        candidates.push({ pattern, span, targetStatus })
    }

    const scanPolePairs = () => {
        if (bearish) {
            for (let hi = pivotHighs.length - 1; hi >= 0; hi--) {
                const H1 = pivotHighs[hi]
                if (H1.index < scanStart) break

                for (let li = pivotLows.length - 1; li >= 0; li--) {
                    const L1 = pivotLows[li]
                    if (L1.index <= H1.index) continue
                    const poleBars = L1.index - H1.index
                    if (poleBars < minPoleBars || poleBars > maxPoleBars) continue

                    const swingPairs = collectPennantSwingPairs(
                        L1.index, L1.price, maxConsBars, pivotHighs, pivotLows, true,
                    )
                    for (const { swingHigh, swingLow } of swingPairs) {
                        tryAddPattern(H1, L1, swingHigh, swingLow)
                    }
                }
            }
        } else {
            for (let li = pivotLows.length - 1; li >= 0; li--) {
                const L1 = pivotLows[li]
                if (L1.index < scanStart) break

                for (let hi = pivotHighs.length - 1; hi >= 0; hi--) {
                    const H1 = pivotHighs[hi]
                    if (H1.index <= L1.index) continue
                    const poleBars = H1.index - L1.index
                    if (poleBars < minPoleBars || poleBars > maxPoleBars) continue

                    const swingPairs = collectPennantSwingPairs(
                        H1.index, H1.price, maxConsBars, pivotHighs, pivotLows, false,
                    )
                    for (const { swingHigh, swingLow } of swingPairs) {
                        tryAddPattern(L1, H1, swingHigh, swingLow)
                    }
                }
            }
        }
    }

    const scanBarPoles = () => {
        const scanEnd = lastBar - pivotLength * 2
        for (let poleEndIdx = scanEnd; poleEndIdx >= scanStart + minPoleBars; poleEndIdx--) {
            for (let poleBars = minPoleBars; poleBars <= maxPoleBars; poleBars++) {
                const poleStartIdx = poleEndIdx - poleBars
                if (poleStartIdx < scanStart) continue

                const consStart = poleEndIdx
                const consEnd = Math.min(lastBar, poleEndIdx + maxConsBars)
                if (consEnd <= consStart + pivotLength) continue

                const consHigh = high.slice(consStart, consEnd + 1)
                const consLow = low.slice(consStart, consEnd + 1)
                const localHighs = findPivotHighs(consHigh, pivotLength, pivotLength)
                    .map((p) => ({ index: p.index + consStart, price: p.price }))
                const localLows = findPivotLows(consLow, pivotLength, pivotLength)
                    .map((p) => ({ index: p.index + consStart, price: p.price }))

                if (bearish) {
                    const poleStartPrice = high[poleStartIdx]
                    const poleEndPrice = low[poleEndIdx]
                    const poleAbs = poleStartPrice - poleEndPrice
                    if (poleAbs <= 0 || poleAbs / poleEndPrice < minPolePct) continue

                    const swingPairs = collectPennantSwingPairs(
                        poleEndIdx, poleEndPrice, maxConsBars, localHighs, localLows, true,
                    )
                    if (!swingPairs.length && localHighs.length && localLows.length) {
                        const swingHigh = localHighs[0]
                        const swingLow = localLows.find((p) => p.index > swingHigh.index) ?? localLows[localLows.length - 1]
                        if (swingLow.index > swingHigh.index) {
                            swingPairs.push({ swingHigh, swingLow })
                        }
                    }
                    for (const { swingHigh, swingLow } of swingPairs) {
                        tryAddPattern(
                            { index: poleStartIdx, price: poleStartPrice },
                            { index: poleEndIdx, price: poleEndPrice },
                            swingHigh,
                            swingLow,
                        )
                    }
                } else {
                    const poleStartPrice = low[poleStartIdx]
                    const poleEndPrice = high[poleEndIdx]
                    const poleAbs = poleEndPrice - poleStartPrice
                    if (poleAbs <= 0 || poleAbs / poleEndPrice < minPolePct) continue

                    const swingPairs = collectPennantSwingPairs(
                        poleEndIdx, poleEndPrice, maxConsBars, localHighs, localLows, false,
                    )
                    if (!swingPairs.length && localLows.length && localHighs.length) {
                        const swingLow = localLows[0]
                        const swingHigh = localHighs.find((p) => p.index > swingLow.index && p.price < poleEndPrice)
                            ?? localHighs[localHighs.length - 1]
                        if (swingHigh.index > swingLow.index && swingHigh.price < poleEndPrice) {
                            swingPairs.push({ swingHigh, swingLow })
                        }
                    }
                    for (const { swingHigh, swingLow } of swingPairs) {
                        tryAddPattern(
                            { index: poleStartIdx, price: poleStartPrice },
                            { index: poleEndIdx, price: poleEndPrice },
                            swingHigh,
                            swingLow,
                        )
                    }
                }
            }
        }
    }

    scanPolePairs()
    scanBarPoles()

    const best = selectBestPennantCandidate(candidates, patternsFilter)
    if (!best) return []

    return limitPatterns([best.pattern], maxPatterns, inProgressSetting)
}

type RoundingBottomCandidate = { points: Point[] }

function detectRoundingBottom(
    high: number[],
    low: number[],
    timestamps: number[],
): RoundingBottomCandidate[] {
    const pivotLength = resolveChartPatternPivotLength()
    const pivotLows = findPivotLows(low, pivotLength, pivotLength)
    if (pivotLows.length < 4) return []

    for (let i = pivotLows.length - 4; i >= 0; i--) {
        const segment = pivotLows.slice(i, i + 4)
        const isBowl = segment.every((pivot, idx) => idx === 0 || pivot.price >= segment[idx - 1].price * 0.995)
        if (!isBowl) continue

        const left = segment[0]
        const right = segment[3]
        const rimHigh = Math.max(...high.slice(left.index, right.index + 1))
        const endIdx = Math.min(timestamps.length - 1, right.index + pivotLength)

        return [{
            points: [
                toPatternPoint('1', timestamps[segment[0].index], segment[0].price),
                toPatternPoint('2', timestamps[segment[1].index], segment[1].price),
                toPatternPoint('3', timestamps[segment[2].index], segment[2].price),
                toPatternPoint('4', timestamps[segment[3].index], segment[3].price),
                toPatternPoint('r', timestamps[left.index], rimHigh),
                toPatternPoint('5', timestamps[endIdx], rimHigh),
            ],
        }]
    }

    return []
}

function detectCupAndHandle(
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions
): ChartPattern[] {
    const rounding = detectRoundingBottom(high, low, timestamps)
    const { priceTargetsFilter = 'All', maxPatterns = 1, inProgress = true } = options
    const pivotLength = resolveChartPatternPivotLength()
    const includeTarget = resolveIncludePriceTarget('CUP_AND_HANDLE', priceTargetsFilter)
    const patterns: ChartPattern[] = []

    for (let cupIdx = rounding.length - 1; cupIdx >= 0; cupIdx--) {
        const cup = rounding[cupIdx]
        const rightPoint = cup.points.find((p) => p.id === '4') ?? cup.points[3]
        const rightTime = rightPoint.x as number
        let rightIdx = -1
        for (let k = 0; k < timestamps.length; k++) {
            if (Math.floor(timestamps[k] / 1000) === rightTime) {
                rightIdx = k
                break
            }
        }
        const handleBars = Math.max(pivotLength, Math.floor(pivotLength * 1.5))
        if (rightIdx < 0 || rightIdx + handleBars >= low.length) continue

        const rimPoint = cup.points.find((p) => p.id === 'r') ?? cup.points[4]
        const rim = rimPoint?.y ?? cup.points[cup.points.length - 1].y
        const handleSliceHigh = high.slice(rightIdx, rightIdx + handleBars + 1)
        const handleSliceLow = low.slice(rightIdx, rightIdx + handleBars + 1)
        const handleEndIdx = rightIdx + handleBars

        const handleUpperLeft = { index: rightIdx, price: rim }
        const handleUpperRight = { index: handleEndIdx, price: Math.max(...handleSliceHigh) }
        const handleLowerLeft = { index: rightIdx, price: Math.min(...handleSliceLow) }
        const handleLowerRight = {
            index: handleEndIdx,
            price: handleLowerLeft.price + (handleUpperRight.price - handleUpperLeft.price),
        }

        if (handleLowerLeft.price >= rim * 0.97) continue
        const upperSlope = pivotSlope(handleUpperLeft, handleUpperRight)
        const lowerSlope = pivotSlope(handleLowerLeft, handleLowerRight)
        const priceScale = rim
        if (!slopesAreParallel(upperSlope, lowerSlope, priceScale, 35)) continue

        const cupDepth = rim - (cup.points.find((p) => p.id === '1')?.y ?? cup.points[0].y)
        const cupPoints = cup.points.filter((p) => ['1', '2', '3', '4', '5'].includes(p.id))
        const patternInProgress = inProgress && handleEndIdx >= low.length - pivotLength

        patterns.push({
            kind: 'CUP_AND_HANDLE',
            bias: 'bullish',
            points: [
                ...cupPoints,
                toPatternPoint('6', timestamps[rightIdx], handleUpperLeft.price),
                toPatternPoint('7', timestamps[rightIdx], handleLowerLeft.price),
                toPatternPoint('8', timestamps[handleEndIdx], handleUpperRight.price),
                toPatternPoint('9', timestamps[handleEndIdx], handleLowerRight.price),
            ],
            priceLineSegments: [
                { from: '1', to: '2' },
                { from: '2', to: '3' },
                { from: '3', to: '4' },
                { from: '4', to: '5' },
                { from: '5', to: '6' },
                { from: '6', to: '8', style: patternInProgress ? 'dotted' : 'solid' },
                { from: '7', to: '9', style: patternInProgress ? 'dotted' : 'solid' },
            ],
            polylineIds: ['1', '2', '3', '4', '5'],
            boundaryLines: [
                { from: '6', to: '8', style: 'dotted' },
                { from: '7', to: '9', style: 'dotted' },
            ],
            fillTriangles: [['1', '2', '4'], ['2', '3', '4']],
            labels: tvNumberedLabels(['1', '2', '3', '4', '5', '6', '7', '8']),
            priceTarget: includeTarget ? rim + cupDepth : undefined,
            targetAnchorPointId: '8',
            inProgress: patternInProgress,
        })
        return limitPatterns(patterns, maxPatterns, inProgress)
    }

    return limitPatterns(patterns, maxPatterns, inProgress)
}

function detectOnRecentWindow(
    detectFn: (high: number[], low: number[], timestamps: number[], options: ChartPatternDetectOptions) => ChartPattern[],
    high: number[],
    low: number[],
    timestamps: number[],
    options: ChartPatternDetectOptions = {}
): ChartPattern[] {
    const lookbackBars = resolveLookbackBars(options.trendHeight)
    const sliced = sliceRecentOhlc(high, low, timestamps, lookbackBars)
    const close = options.close
    const closeSlice = close?.length === high.length
        ? close.slice(close.length - sliced.high.length)
        : close
    const inProgress = resolveBooleanOption(options.inProgress, true)
    return detectFn(sliced.high, sliced.low, sliced.timestamps, {
        maxPatterns: 1,
        ...options,
        inProgress,
        close: closeSlice?.length === sliced.high.length
            ? closeSlice
            : sliced.high.map((h, i) => (h + sliced.low[i]) / 2),
    })
}

export function detectDoubleTopPattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow(detectDoubleTop, high, low, timestamps, options) }

export function detectDoubleBottomPattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow(detectDoubleBottom, high, low, timestamps, options) }

export function detectCupAndHandlePattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow(detectCupAndHandle, high, low, timestamps, options) }

export function detectRisingWedgePattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow(detectRisingWedge, high, low, timestamps, options) }

export function detectFallingWedgePattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow(detectFallingWedge, high, low, timestamps, options) }

export function detectBearishPennantPattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow((h, l, t, o) => detectPennant(h, l, t, o, 'BEARISH_PENNANT'), high, low, timestamps, options) }

export function detectBullishPennantPattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow((h, l, t, o) => detectPennant(h, l, t, o, 'BULLISH_PENNANT'), high, low, timestamps, options) }

export function detectAscendingTrianglePattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow(detectAscendingTriangle, high, low, timestamps, options) }

export function detectDescendingTrianglePattern(
    high: number[], low: number[], timestamps: number[], options?: ChartPatternDetectOptions
) { return detectOnRecentWindow(detectDescendingTriangle, high, low, timestamps, options) }
